import { useTitropEvents } from '@/composables/useTitropEvents.ts'
import { useActiveElement, useEventBus, UseEventBusReturn, useLocalStorage, useMagicKeys } from '@vueuse/core'
import { capitalCase } from 'change-case'

interface HotkeyEventCommon {
  preventDefault?: boolean
  hotkey: string
  help: string
  group?: string
}

interface HotkeyEventNoPayload extends HotkeyEventCommon {
  event: UseEventBusReturn<never, never> | (() => void | Promise<void>)
}

interface HotkeyEventWithPayload<T> extends HotkeyEventCommon {
  event: UseEventBusReturn<never, T> | (() => void | Promise<void>)
  payload: T
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HotkeyEventEntry = HotkeyEventNoPayload | HotkeyEventWithPayload<any>

const keys = useMagicKeys({
  passive: false,
  onEventFired(e) {
    const def = hotkeyEvents.find((k) => k.hotkey == normalizeKeyEvent(e))
    if (!def) return
    if (def.preventDefault) e.preventDefault()
  }
})

function normalizeKeyEvent(e: KeyboardEvent): string {
  const keys: string[] = []
  if (e.ctrlKey) keys.push('ctrl')
  if (e.shiftKey) keys.push('shift')
  if (e.altKey) keys.push('alt')
  keys.push(e.key)
  return keys.join('+').toLowerCase()
}

//
// const { ctrl_shift_f, ctrl_g, ctrl_1, ctrl_2, ctrl_3, ctrl_4, ctrl_5, ctrl_6, ctrl_7, ctrl_8, ctrl_9, ctrl_0, alt_m, alt_n, alt_s, alt_enter, ctrl_alt_d, f1, ctrl_n } = useMagicKeys({
//   passive: false,
//   onEventFired(e) {
//     if (hotkeyWhitelist.has(normalizeKeyEvent(e))) e.preventDefault()
//   }
// })

//simplifies direct event trigger
const hotkeyEvents: HotkeyEventEntry[] = [
  { hotkey: 'ctrl+alt+d', event: useTitropEvents().hotkeyToggleDeveloperDialog, group: 'Global', help: 'Developer-Dialog umschalten', preventDefault: true },
  { hotkey: 'ctrl+shift+f', event: useTitropEvents().hotkeyFocusSearch, group: 'Global', help: 'Suche fokusieren', preventDefault: true },
  { hotkey: 'alt+s', event: useTitropEvents().hotkeyToggleStatusChangeDialog, group: 'Global', help: 'Statusänderung', preventDefault: true },
  { hotkey: 'f1', event: useTitropEvents().hotkeyToggleHotkeyHelpDialog, group: 'Global', help: 'Diesen Hilfe-Dialog für Tastenkürzel anzeigen', preventDefault: true },
  { hotkey: 'ctrl+g', event: useTitropEvents().hotkeyGotoEffortToggle, group: 'Global', help: 'Direkt zu Leistungsobjekt springen - anhand ID-Eingabe', preventDefault: true },
  { hotkey: 'alt+n', event: useTitropEvents().hotkeyToggleNoticesSidebar, group: 'Global', help: 'persönliche Notizen umschalten', preventDefault: true },
  { hotkey: 'alt+a', event: useTitropEvents().hotkeyToggleActivityFeed, group: 'Global', help: '(aktuell inaktiv) Aktivitätsfeed anzeigen/verstecken', preventDefault: true },
  {
    hotkey: 'alt+m',
    event: () => {
      const activityFeedStatus = useLocalStorage('navigation-visible', true, { writeDefaults: true })
      activityFeedStatus.value = !activityFeedStatus.value
    },
    help: 'Navigationsleiste/Sidebar umschalten',
    preventDefault: true
  },
  {
    hotkey: 'alt+enter',
    event: () => {
      ;(activeForm.value as HTMLFormElement)?.requestSubmit()
    },
    group: 'Divers',
    help: 'Formular direkt absenden (nicht überall unterstützt)',
    preventDefault: true
  },

  { hotkey: 'ctrl+1', event: useTitropEvents().hotkeyChangeSearchScope, payload: { scope: 0 }, group: 'Im Suchfeld', help: 'Suchbereich auf Scope "überall" setzen (nur im Suchfeld)', preventDefault: true },
  { hotkey: 'ctrl+2', event: useTitropEvents().hotkeyChangeSearchScope, payload: { scope: 1 }, group: 'Im Suchfeld', help: 'Suchbereich auf Scope "Geschäftspartner" setzen (nur im Suchfeld)', preventDefault: true },
  { hotkey: 'ctrl+3', event: useTitropEvents().hotkeyChangeSearchScope, payload: { scope: 2 }, group: 'Im Suchfeld', help: 'Suchbereich auf Scope "Auftragszeilen" setzen (nur im Suchfeld)', preventDefault: true },
  { hotkey: 'ctrl+4', event: useTitropEvents().hotkeyChangeSearchScope, payload: { scope: 3 }, group: 'Im Suchfeld', help: 'Suchbereich auf Scope "Service-Calls" setzen (nur im Suchfeld)', preventDefault: true },
  { hotkey: 'ctrl+5', event: useTitropEvents().hotkeyChangeSearchScope, payload: { scope: 4 }, group: 'Im Suchfeld', help: 'Suchbereich auf Scope "Mitarbeiter" setzen (nur im Suchfeld)', preventDefault: true },
  { hotkey: 'ctrl+6', event: useTitropEvents().hotkeyChangeSearchScope, payload: { scope: 5 }, group: 'Im Suchfeld', help: 'Suchbereich auf Scope "Modul" setzen (nur im Suchfeld)', preventDefault: true },
  { hotkey: 'ctrl+7', event: useTitropEvents().hotkeyChangeSearchScope, payload: { scope: 6 }, group: 'Im Suchfeld', help: 'Suchbereich auf Scope "Infoboard" setzen (nur im Suchfeld)', preventDefault: true },
  { hotkey: 'ctrl+8', event: useTitropEvents().hotkeyChangeSearchScope, payload: { scope: 7 }, group: 'Im Suchfeld', help: 'Suchbereich auf Scope "Anfragenverwaltung" setzen (nur im Suchfeld)', preventDefault: true },
  { hotkey: 'ctrl+9', event: useTitropEvents().hotkeyChangeSearchScope, payload: { scope: 8 }, group: 'Im Suchfeld', help: 'Suchbereich auf Scope "Auftrag" setzen (nur im Suchfeld)', preventDefault: true }
  // { hotkey: 'ctrl+0', event: useTitropEvents().hotkeyChangeSearchScope, payload: { scope: 9 }, help: 'Suchbereich auf Scope "überall" setzen (nur im Suchfeld)' }
]

export const hotkeysHelp = computed<({ key: string; description: string } | string)[]>(() => {
  const res: ({ key: string; description: string } | string)[] = []

  hotkeyEvents.forEach((k) => {
    if (k.group && !res.includes(k.group)) res.push(k.group)
    res.push({
      key: k.hotkey
        .split('+')
        .map((s) => capitalCase(s))
        .join(' + '),
      description: k.help
    })
  })

  return res
})

const activeElement = useActiveElement()
const activeForm = computed(() => {
  let res: HTMLElement | null | undefined = activeElement.value
  while (res?.nodeName != 'FORM' && res?.parentNode) {
    res = res.parentNode as HTMLElement
  }
  if (res == (document as unknown)) return undefined
  return res
})

export default {
  install() {
    const newImprovementFormIds = ['imprvmntTitle', 'imprvmntText', 'imprvmntScope']
    const newTopicFormIds = ['newTopicText', 'newTopicTitle']

    hotkeyEvents.forEach((k) => {
      watch(keys[k.hotkey], (v) => {
        if (!v) return
        if (typeof k.event == 'function') k.event()
        else (k.event as UseEventBusReturn<never, unknown>).emit(k['payload'])
      })
    })

    watchEffect(() => {
      if (activeElement.value) {
        if (activeElement.value.id === 'search-field') {
          // if (ctrl_1.value) useTitropEvents().hotkeyChangeSearchScope.emit(undefined, { scope: 0 })
          // else if (ctrl_2.value) useTitropEvents().hotkeyChangeSearchScope.emit(undefined, { scope: 1 })
          // else if (ctrl_3.value) useTitropEvents().hotkeyChangeSearchScope.emit(undefined, { scope: 2 })
          // else if (ctrl_4.value) useTitropEvents().hotkeyChangeSearchScope.emit(undefined, { scope: 3 })
          // else if (ctrl_5.value) useTitropEvents().hotkeyChangeSearchScope.emit(undefined, { scope: 4 })
          // else if (ctrl_6.value) useTitropEvents().hotkeyChangeSearchScope.emit(undefined, { scope: 5 })
          // else if (ctrl_7.value) useTitropEvents().hotkeyChangeSearchScope.emit(undefined, { scope: 6 })
          // else if (ctrl_8.value) useTitropEvents().hotkeyChangeSearchScope.emit(undefined, { scope: 7 })
          // else if (ctrl_9.value) useTitropEvents().hotkeyChangeSearchScope.emit(undefined, { scope: 8 })
          // else if (ctrl_0.value) useTitropEvents().hotkeyChangeSearchScope.emit(undefined, { scope: 9 })
        }
        if (keys['alt+enter'].value) {
          if (newImprovementFormIds.includes(activeElement.value.id) || (activeElement.value.offsetParent && newImprovementFormIds.includes(activeElement.value.offsetParent.id))) useEventBus('keyshort_new_improvement_submit_form').emit()
          if (newTopicFormIds.includes(activeElement.value.id) || (activeElement.value.offsetParent && newTopicFormIds.includes(activeElement.value.offsetParent.id))) useEventBus('keyshort_new_topic_submit_form').emit()
          if (activeElement.value.id === 'newImpThreadTextForm' || activeElement.value.offsetParent?.id === 'newImpThreadTextForm') useEventBus('keyshort_new_thread_submit_form').emit()
          if (activeForm.value?.id === 'newInfoboardEntryForm') useEventBus('keyshort_new_infoboard_entry_submit_form').emit()
          if (activeForm.value?.id === 'newInfoboardResponseForm') useEventBus('keyshort_new_infoboard_response_submit_form').emit()
          //var test = activeElement
          //debugger
        }
      }
    })
  }
}
